import { memo } from 'react'
import styled, { createGlobalStyle, keyframes } from 'styled-components'
import { m as motion } from 'framer-motion'
import reset from 'styled-reset'
import { generateMedia } from 'styled-media-query'

export const dash = keyframes`
  to {
    stroke-dashoffset: 0;
  }
`

export const dashOut = keyframes`
  from {
    stroke-dashoffset: 0;
  }
  to {
    stroke-dashoffset: 200;
  }
`

export const media = generateMedia({
  desktop19: '1900px',
  desktop16: '1680px',
  desktop15: '1536px',
  desktop14: '1440px',
  desktop13: '1360px',
  desktop12: '1280px',
  desktop: '1025px',
  tablet: '1024px',
  miniTablet: '860px',
  ipad: '768px',
  phone: '420px',
  miniPhone: '340px',
})

export const theme = {
  white: '#FFFFFF',
  blue: '#25B0C9',
  darkBlue: '#0089A2',
  green: '#73C09C',
  lightGreen: '#CCE7EC',
  orange: '#FFA700',
  gray: '#5A5B5E',
  textGray: '#777777',
  lightGray: '#B6BBC8',
  superLightGray: '#D3D7DF',
  blackTransparent: 'rgba(0,0,0,0.7)',
  whiteTransparent: 'rgba(255,255,255,0.6)',
}

export const GlobalStyles = createGlobalStyle`
  ${reset}
  
  * {
    text-decoration: none;
    box-sizing: border-box;
  }

  html, body, #root {
    width: 100%;
    height: 100%;
    overflow: hidden;
    ${media.lessThan('phone')`
      height: auto;
      min-height: calc(var(--vh, 1vh) * 100);
    `};
  }

  html {
    box-sizing: border-box;
    -webkit-font-smoothing: antialiased;
    ${media.greaterThan('desktop13')`
      font-size: 12px;
    `};
    ${media.greaterThan('desktop14')`
      font-size: 13px;
    `};
    ${media.greaterThan('desktop15')`
      font-size: 14px;
    `};
    ${media.greaterThan('desktop16')`
      font-size: 15px;
    `};
    ${media.greaterThan('desktop19')`
      font-size: 16px;
    `};
    ${media.lessThan('desktop12')`
      font-size: 12px;
    `};
    ${media.lessThan('tablet')`
      font-size: 14px;
    `};
    ${media.lessThan('miniTablet')`
      font-size: 13px;
    `};
    ${media.lessThan('phone')`
      overflow-y: auto;
      font-size: 12px;
    `};
  }

  body {
    font-family: 'Raleway', sans-serif;
    overscroll-behavior: none;
  }

  button, a {
    cursor: pointer;
    font-family: inherit;
    padding: 0;
    outline: none;
    border: none;
    background: none;
  }

  p {
    strong {
      font-weight: 700;
    }
    em {
      font-style: italic;
    }
  }

  svg {
    backface-visibility: hidden !important;
    transform: translateZ(0) scale(1.0, 1.0);
  }
`

export const AppContainer = styled.div`
  position: relative;
  width: calc(var(--vw, 1vw) * 100);
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
  ${media.lessThan('phone')`
    height: auto;
    min-height: calc(var(--vh, 1vh) * 100);
  `};
`

export const Main = styled.main`
  position: relative;
  width: calc(var(--vw, 1vw) * 100);
  height: calc(var(--vh, 1vh) * 100);
  overflow: hidden;
  ${media.lessThan('phone')`
    height: auto;
    min-height: calc(var(--vh, 1vh) * 100);
  `};
`

export const BackToButton = memo(styled(motion.button)`
  position: relative;
  width: 3rem;
  height: 3rem;
  display: flex;
  flex-flow: column nowrap;
  justify-content: center;
  align-items: center;
  background: none;
  border: none;
  z-index: 11;
  & > svg {
    position: absolute;
    width: 110%;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    path {
      stroke-dasharray: 200;
      stroke-dashoffset: 200;
      stroke: black !important;
      animation: ${dash} 1s linear forwards;
      animation-delay: 0.5s;
    }
  }
  div {
    position: relative;
    width: 1.625rem;
    height: 1.625rem;
    display: grid;
    place-items: center;
    transform: rotate(-180deg);
    svg {
      width: 100%;
      transform: translateZ(0) scale(1.0, 1.0);
      ${media.greaterThan('phone')`
        transition: all 300ms linear;
        transition-property: fill;
        will-change: fill;
      `}
    }
  }
  &:hover {
    & > svg {
      path {
        animation: ${dashOut} 0.5s linear forwards;
        animation-delay: 0s;
      }
    }
    div svg {
      fill: ${(props) => props.theme.blue};
    }
  }

  &.backToTeam {
    position: fixed;
    right: 2.5rem;
    top: 3rem;
    ${media.lessThan('phone')`
      right: 1.5625rem;
      top: 1.5625rem;
    `}
    & > svg {
      path {
        stroke: white !important;
      }
    }
    div {
      transform: rotate(-90deg);
      svg {
        fill: white;
      }
    }
    ${media.greaterThan('phone')`
      &:hover {
        div {
          svg {
            fill: ${(props) => props.theme.darkBlue};
          }
        }
      }
    `}
  }
`)

export const Section = memo(styled(motion.section)`
  position: absolute;
  left: 0;
  top: 0;
  width: calc(var(--vw, 1vw) * 100);
  height: calc(var(--vh, 1vh) * 100);
  background: transparent;
  ${media.lessThan('phone')`
    height: auto;
    min-height: calc(var(--vh, 1vh) * 100);
  `};
`)

export const Title = memo(styled(motion.h2)`
  font-size: 5.0625rem;
  line-height: 4.25rem;
  font-weight: 300;
  letter-spacing: -0.04rem;
  margin-bottom: 2rem;
  ${media.lessThan('phone')`
    font-size: 3.75rem;
    line-height: 3.25rem;
  `};
  ${media.lessThan('miniPhone')`
    font-size: 2.8125rem;
    line-height: 3.125rem;
  `};
`)

export const Description = memo(styled(motion.div)`
  p {
    font-size: 1.625rem;
    line-height: 1.9375rem;
    font-weight: 300;
    margin-bottom: 2.5rem;
    &:last-child {
      margin: 0;
    }
    ${media.lessThan('phone')`
      margin-bottom: 1.5rem;
      &:not(:first-child) {
        display: none;
      }
    `}
  }

  .readMoreMobile {
    display: none;
    font-size: 1.375rem;
    font-weight: 700;
    margin-top: 1.25rem;
    ${media.lessThan('phone')`
      display: block;
    `}
  }

  ${media.lessThan('phone')`
    &.showMore {
      p {
        display: block !important;
      }
    }
  `}
`)

export const LogoWrapper = memo(styled(motion.button)`
  position: fixed;
  width: 10.75rem;
  height: 0;
  padding-top: 4.75rem;
  left: 2.5rem;
  top: 2.5rem;
  font-size: 0;
  z-index: 2;
  ${media.greaterThan('phone')`
    transition: opacity 300ms ease-out, z-index 0ms linear 500ms;
    &:hover {
      opacity: 0.6 !important;
    }
  `}
  svg {
    path {
      ${media.greaterThan('phone')`
        transition: all 300ms;
        transition-property: fill;
        will-change: fill;
        &.white {
          fill: white !important;
        }
      `}
    }
  }
  ${media.lessThan('phone')`
    left: 0.875rem;
    top: 0.875rem;
    div:last-of-type {
      display: none;
    }
  `};
`)

export const LogoIconWrapper = memo(styled(motion.div)`
  position: absolute;
  width: 42.8571%;
  top: 0;
  left: 0;
  svg {
    path {
      &#logo-icon-grad-1 {
        fill: url(${`${window.location.protocol}//${window.location.host}${window.location.pathname}#laborgg-logo-icon-grad-1`});
        ${media.lessThan('tablet')`
          fill: ${(props) => props.theme.green};
        `};
      }
      &#logo-icon-grad-2 {
        fill: url(${`${window.location.protocol}//${window.location.host}${window.location.pathname}#laborgg-logo-icon-grad-2`});
        ${media.lessThan('tablet')`
          fill: ${(props) => props.theme.blue};
        `};
      }
      &#logo-icon-grad-3 {
        fill: url(${`${window.location.protocol}//${window.location.host}${window.location.pathname}#laborgg-logo-icon-grad-3`});
        ${media.lessThan('tablet')`
          fill: ${(props) => props.theme.orange};
        `};
      }
    }
  }
`)

export const LogoTextWrapper = memo(styled(motion.div)`
  position: absolute;
  width: 57.3412%;
  top: 50%;
  right: 0;
  transform: translateY(-50%);
`)

export const SocialLinks = memo(styled(motion.div)`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  a {
    display: block;
    width: 1.875rem;
    margin-right: 1.375rem;
    ${media.greaterThan('phone')`
      svg {
        transition: all 300ms linear;
        transition-property: fill, stroke;
        will-change: fill, stroke;
      }
    `}

    &.instagram {
      svg {
        fill: none;
        stroke: black;
        path[data-name='inner'],
        path[data-name='outer'] {
          fill: black;
        }
      }
    }
    ${media.greaterThan('phone')`
      &:hover {
        svg {
          fill: ${(props) => props.theme.orange};
        }
        &.instagram {
          svg {
            fill: none;
            stroke: ${(props) => props.theme.orange};
            path[data-name='inner'],
            path[data-name='outer'] {
              fill: ${(props) => props.theme.orange};
            }
          }
        }
      }
    `}
  }
  &.sidemenu {
    position: absolute;
    left: 5rem;
    bottom: 3.5rem;
    z-index: 12;
     ${media.lessThan('phone')`
      left: 4rem;
      bottom: 2.5rem;
    `}
    ${media.lessThan('miniPhone')`
      left: 2rem;
    `}
    a {
      svg {
        fill: white;
      }
      &.instagram {
        svg {
          fill: none;
          stroke: white;
          path[data-name='inner'],
          path[data-name='outer'] {
            fill: white;
          }
        }
      }
      ${media.greaterThan('phone')`
        &:hover {
          svg {
            fill: ${(props) => props.theme.lightGreen};
          }
          &.instagram {
            svg {
              fill: none;
              stroke: ${(props) => props.theme.lightGreen};
              path[data-name='inner'],
              path[data-name='outer'] {
                fill: ${(props) => props.theme.lightGreen};
              }
            }
          }
        }
      `}
    }
  }
`)

export const Element = memo(styled.div`
  position: absolute;
  height: 0;
  pointer-events: none;
  width: 100vh;
  width: calc(var(--vh, 1vh) * 100);
  padding-top: 100vh;
  padding-top: calc(var(--vh, 1vh) * 100);
  transform-origin: center !important;
  .main-bg & {
    transition: all 1500ms ease-out;
    transition-property: transform;
    will-change: transform;
  }

  ${media.lessThan('tablet')`
    width: 100vw;
    width: calc(var(--vw, 1vw) * 100);
    padding-top: 100vw;
    padding-top: calc(var(--vw, 1vw) * 100);
  `};

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 50%;
    top: 50%;
    pointer-events: none;
    transform: translate(-50%, -50%);
    path {
      transition: all 1000ms;
      transition-property: d, fill;
      will-change: d, fill;
    }
  }
`)

export const MElement = memo(styled(motion.div)`
  position: absolute;
  height: 0;
  pointer-events: none;
  width: 100vh;
  width: calc(var(--vh, 1vh) * 100);
  padding-top: 100vh;
  padding-top: calc(var(--vh, 1vh) * 100);
  ${media.lessThan('tablet')`
    width: 100vw;
    width: calc(var(--vw, 1vw) * 100);
    padding-top: 100vw;
    padding-top: calc(var(--vw, 1vw) * 100);
  `};

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
    left: 50%;
    top: 50%;
    pointer-events: none;
    transform: translate(-50%, -50%);
    path {
      transition: all 1000ms;
      transition-property: d, fill;
      will-change: d, fill;
    }
  }
`)

export const NotFoundSection = styled(Section)`
  position: fixed;
  left: 0;
  top: 0;
  display: grid;
  place-items: center;
  background: ${(props) => props.theme.blue};
`

export const NotFoundContainer = styled.div`
  width: 65%;
  padding-right: 40%;
  color: white;
  ${media.lessThan('tablet')`
    width: 80%;
    padding-right: 0;
    margin-bottom: 5rem;
  `};

  h3 {
    font-size: 5.0625rem;
    line-height: 5.9375rem;
    font-weight: 700;
    margin: 0 0 2rem;
    ${media.lessThan('phone')`
      font-size: 4.125rem;
      line-height: 5rem;
    `};
    span {
      display: block;
      font-size: 4.125rem;
      line-height: 4.8125rem;
      font-weight: 300;
      ${media.lessThan('phone')`
        font-size: 3.1875rem;
        line-height: 3.875rem;
      `};
    }
  }

  a {
    display: inline-block;
    font-size: 1.125rem;
    line-height: 1.3125rem;
    font-weight: 700;
    text-transform: uppercase;
    padding-bottom: 0.5rem;
    border-bottom: 1px solid white;
    color: white;
    ${media.greaterThan('phone')`
      transition: all 300ms linear;
      transition-property: color, border-color;
      will-change: color, border-color;
      &:hover {
        color: ${(props) => props.theme.darkBlue};
        border-color: ${(props) => props.theme.darkBlue};
      }
    `}
    ${media.lessThan('phone')`
      font-size: 1.0625rem;
      line-height: 1.25rem;
    `};
  }
`

export const NotFoundIcon = styled.div`
  position: fixed;
  width: 40%;
  height: 0;
  padding-top: 15%;
  right: 15%;
  bottom: 16%;
  ${media.lessThan('tablet')`
    width: 60%;
    padding-top: 22.5%;
    right: 6%;
    bottom: 10%;
  `};
  &::before {
    content: '';
    position: absolute;
    right: 0;
    top: 43%;
    width: 100%;
    height: 0.3125rem;
    background: white;
    transform: rotate(-24deg);
    ${media.greaterThan('phone')`
      transition: transform 200ms ease-in;
      will-change: transform;
    `}
  }
  svg {
    position: absolute;
    width: 12%;
    left: 50%;
    bottom: 0;
    transform: translateX(-50%);
  }
  ${media.greaterThan('phone')`
    &:hover {
      &::before {
        transition-duration: 650ms;
        transition-timing-function: ease-out;
        transform: rotate(24deg);
      }
    }
  `}
`